import React from "react";
import {
  Megaphone, Blocks, ShieldCheck, Building2, FileHeart, Receipt,
  CalendarClock, CreditCard, MessageCircle, Phone, Database,
  Server, Lock, MapPin, Facebook, Instagram, CheckCircle2,
} from "lucide-react";
import { Block } from "./FlowBlocks";
import { campaignSteps, campaignTypes, integrationSystems, residencyLayers, residencyPoints } from "../../mock";

const sysIcons = { Building2, FileHeart, Receipt, CalendarClock, CreditCard, MessageCircle, Phone };

// ---- 7. Social media campaigning
export const CampaignBlock = ({ data, index }) => (
  <Block id={data.id} index={index} icon={Megaphone} title={data.title} tagline={data.tagline} desc={data.desc} bullets={data.bullets}>
    <div>
      <div className="grid gap-3 sm:grid-cols-2">
        {campaignSteps.map((s) => (
          <div key={s.step} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-full border border-cyan-400/30 bg-cyan-400/10 font-display text-xs font-bold text-cyan-300">
              {s.step}
            </div>
            <div className="mt-3 text-sm font-semibold text-white">{s.title}</div>
            <div className="mt-1 text-xs leading-relaxed text-slate-500">{s.desc}</div>
          </div>
        ))}
      </div>

      {/* WhatsApp-style campaign preview */}
      <div className="mt-5 rounded-2xl border border-white/10 bg-black/30 p-4">
        <div className="mb-3 flex items-center gap-2 border-b border-white/5 pb-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-cyan-400/15"><Building2 className="h-4 w-4 text-cyan-300" /></div>
          <div>
            <div className="text-xs font-semibold text-white">City Hospital <CheckCircle2 className="ml-0.5 inline h-3 w-3 text-emerald-400" /></div>
            <div className="text-[9px] text-slate-500">Your Health, Our Priority</div>
          </div>
        </div>
        <div className="max-w-[85%] rounded-xl rounded-tl-sm border border-white/10 bg-white/[0.05] p-3 text-xs leading-relaxed text-slate-300">
          Stay safe this season! Tips to prevent seasonal flu and keep your family healthy.
          <span className="mt-1.5 block rounded-md bg-cyan-400/10 px-2 py-1 text-center text-[10px] font-semibold text-cyan-300">Stay Healthy This Season — Learn More</span>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {campaignTypes.map((c) => (
          <span key={c} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-medium text-slate-300">{c}</span>
        ))}
        <span className="ml-auto flex items-center gap-2 text-slate-500">
          <Facebook className="h-4 w-4" /> <Instagram className="h-4 w-4" />
        </span>
      </div>
    </div>
  </Block>
);

// ---- 8. Hospital system integrations (hub & spoke)
export const IntegrationsBlock = ({ data, index }) => (
  <Block id={data.id} index={index} icon={Blocks} title={data.title} tagline={data.tagline} desc={data.desc} bullets={data.bullets} flip>
    <div>
      <div className="relative rounded-2xl border border-white/10 bg-black/30 p-6">
        {/* hub */}
        <div className="mx-auto flex w-fit flex-col items-center rounded-2xl border border-cyan-400/30 bg-cyan-400/10 px-6 py-4 text-center">
          <span className="font-display text-sm font-bold text-white">ARKISION AI</span>
          <span className="text-[10px] text-cyan-300/80">Patient Engagement Platform</span>
        </div>
        {/* spokes */}
        <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
          {integrationSystems.map((s) => {
            const Icon = sysIcons[s.icon];
            return (
              <div key={s.name} className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 transition-colors duration-200 hover:border-cyan-400/30">
                <Icon className="h-4 w-4 shrink-0 text-cyan-300" />
                <span className="text-xs font-medium text-slate-200">{s.name}</span>
              </div>
            );
          })}
          <div className="flex items-center gap-2 rounded-xl border border-dashed border-white/15 px-3 py-2.5">
            <Blocks className="h-4 w-4 shrink-0 text-slate-500" />
            <span className="text-xs text-slate-500">+ custom APIs</span>
          </div>
        </div>
      </div>
    </div>
  </Block>
);

// ---- 9. Data residency
export const ResidencyBlock = ({ data, index }) => (
  <Block id={data.id} index={index} icon={ShieldCheck} title={data.title} tagline={data.tagline} desc={data.desc} bullets={data.bullets}>
    <div>
      {/* layer stack */}
      <div className="relative space-y-2.5 rounded-2xl border border-cyan-400/20 bg-cyan-400/[0.03] p-5">
        <span className="absolute -top-2.5 left-5 flex items-center gap-1 rounded-full border border-cyan-400/30 bg-[#04060a] px-3 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-cyan-300">
          <MapPin className="h-3 w-3" /> Inside your country
        </span>
        {residencyLayers.map((l, i) => (
          <React.Fragment key={l.name}>
            <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
              <span className="flex items-center gap-2.5 text-sm font-medium text-slate-200">
                {i < 2 ? <Database className="h-4 w-4 text-cyan-300" /> : i < 4 ? <Server className="h-4 w-4 text-cyan-300" /> : <Lock className="h-4 w-4 text-emerald-300" />}
                {l.name}
              </span>
              <span className="text-[10px] font-medium text-emerald-300">{l.location}</span>
            </div>
            {i < residencyLayers.length - 1 && <div className="mx-auto h-3 w-px bg-cyan-400/30" />}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-5 space-y-2">
        {residencyPoints.map((p) => (
          <div key={p} className="flex items-center gap-2.5 text-sm text-slate-300">
            <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-400" /> {p}
          </div>
        ))}
      </div>
    </div>
  </Block>
);
